import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BulkPaymentMatch } from '../../../models/bulk-payment.model';
import { MatIconModule } from '@angular/material/icon';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'app-bulk-payment-review-step',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CurrencyPipe,
    MatIconModule,
    MatTableModule,
    MatTooltipModule,
    MatCheckboxModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './review-step.component.html',
})
export class BulkPaymentReviewStepComponent {
  @Input() matches: BulkPaymentMatch[] = [];
  @Input() costOfCapitalPercent: number = 0;
  @Input() applyCostOfCapital: boolean = false;
  @Output() costOfCapitalChange = new EventEmitter<boolean>();
  @Output() addCustomer = new EventEmitter<BulkPaymentMatch>();
  @Output() selectCustomer = new EventEmitter<BulkPaymentMatch>();
  @Output() selectPaymentMethod = new EventEmitter<BulkPaymentMatch>();
  @Output() addCard = new EventEmitter<BulkPaymentMatch>();
  @Output() amountEdited = new EventEmitter<{ match: BulkPaymentMatch; rawAmount: string }>();
  @Output() invoiceEdited = new EventEmitter<{ match: BulkPaymentMatch; rawInvoice: string }>();

  displayedColumns: string[] = ['status', 'accountNumber', 'customerName', 'amount', 'invoiceNumber', 'paymentMethod'];
  issueDisplayedColumns: string[] = [
    'status',
    'accountNumber',
    'customerName',
    'amount',
    'invoiceNumber',
    'error',
    'actions',
  ];

  /** Rows that are fully ready to process: matched customer + payment method + valid amount + valid invoice */
  get readyPayments(): BulkPaymentMatch[] {
    return this.matches.filter((m) => m.status === 'matched' && m.row.isAmountValid && m.row.isInvoiceValid);
  }

  /** Rows that need attention: any customer issue OR invalid amount OR missing invoice */
  get issuePayments(): BulkPaymentMatch[] {
    return this.matches.filter((m) => m.status !== 'matched' || !m.row.isAmountValid || !m.row.isInvoiceValid);
  }

  // Keep for backward-compat with template sections that still reference these
  get matchedPayments(): BulkPaymentMatch[] {
    return this.readyPayments;
  }

  get unmatchedPayments(): BulkPaymentMatch[] {
    return this.issuePayments;
  }

  get totalRowCount(): number {
    return this.matches.length;
  }

  get readyCount(): number {
    return this.readyPayments.length;
  }

  get customerIssueCount(): number {
    return this.matches.filter((m) => m.status !== 'matched').length;
  }

  get invalidAmountCount(): number {
    return this.matches.filter((m) => !m.row.isAmountValid).length;
  }

  get missingInvoiceCount(): number {
    return this.matches.filter((m) => !m.row.isInvoiceValid).length;
  }

  /** Total amount sums only ready-to-process rows */
  get totalAmount(): number {
    return this.readyPayments.reduce((sum, match) => sum + match.row.amount, 0);
  }

  get totalCostOfCapital(): number {
    if (!this.applyCostOfCapital) return 0;
    return this.matchedPayments.reduce((sum, match) => {
      // Only apply COC to credit card payments
      if (match.paymentMethod?.accountType !== 'ACH') {
        return sum + (match.row.costOfCapitalAmount || 0);
      }
      return sum;
    }, 0);
  }

  get grandTotal(): number {
    return this.totalAmount + this.totalCostOfCapital;
  }

  get isCostOfCapitalAvailable(): boolean {
    return this.costOfCapitalPercent > 0;
  }

  get hasCreditCardPayments(): boolean {
    return this.matchedPayments.some((m) => m.paymentMethod?.accountType !== 'ACH');
  }

  onCostOfCapitalToggle(): void {
    this.costOfCapitalChange.emit(this.applyCostOfCapital);
  }

  getCostOfCapitalForMatch(match: BulkPaymentMatch): number {
    if (!this.applyCostOfCapital || match.paymentMethod?.accountType === 'ACH') {
      return 0;
    }
    return match.row.costOfCapitalAmount || 0;
  }

  getDisplayedColumnsForMatched(): string[] {
    if (this.applyCostOfCapital && this.hasCreditCardPayments) {
      return ['status', 'accountNumber', 'customerName', 'amount', 'costOfCapital', 'invoiceNumber', 'paymentMethod'];
    }
    return this.displayedColumns;
  }

  onAddCustomer(match: BulkPaymentMatch): void {
    this.addCustomer.emit(match);
  }

  onSelectCustomer(match: BulkPaymentMatch): void {
    this.selectCustomer.emit(match);
  }

  onSelectPaymentMethod(match: BulkPaymentMatch): void {
    this.selectPaymentMethod.emit(match);
  }

  onAddCard(match: BulkPaymentMatch): void {
    this.addCard.emit(match);
  }

  onAmountBlur(match: BulkPaymentMatch, newRawAmount: string): void {
    this.amountEdited.emit({ match, rawAmount: newRawAmount });
  }

  onInvoiceBlur(match: BulkPaymentMatch, newRawInvoice: string): void {
    this.invoiceEdited.emit({ match, rawInvoice: newRawInvoice });
  }

  hasPaymentMethods(match: BulkPaymentMatch): boolean {
    // Check if customer has any payment methods available
    return match.customer !== undefined;
  }

  getStatusIcon(status: string): string {
    switch (status) {
      case 'matched':
        return 'check_circle';
      case 'no-customer':
        return 'error';
      case 'no-payment-method':
        return 'warning';
      default:
        return 'help';
    }
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'matched':
        return 'status-success';
      case 'no-customer':
        return 'status-error';
      case 'no-payment-method':
        return 'status-warning';
      default:
        return 'status-default';
    }
  }

  getDisplayedColumnsForIssues(): string[] {
    return this.issueDisplayedColumns;
  }

  /**
   * Compose the full issue message for a row.
   * Used both as the cell text and as the cell's matTooltip so the merchant can
   * read the entire reason even when the column is narrow and the text ellipses.
   */
  getIssueText(match: BulkPaymentMatch): string {
    const parts: string[] = [];
    if (match.error) {
      parts.push(match.error);
    }
    if (!match.row.isAmountValid) {
      const raw = match.row.rawAmount?.trim() || '(empty)';
      parts.push(`Invalid amount: "${raw}"`);
    }
    if (!match.row.isInvoiceValid) {
      parts.push('Missing invoice number');
    }
    return parts.join(' · ');
  }

  getPaymentMethodDisplay(match: BulkPaymentMatch): string {
    if (!match.paymentMethod) {
      return '-';
    }

    const method = match.paymentMethod;
    if (method.accountType === 'ACH') {
      return `ACH ${method.account?.slice(-4) || ''}`;
    } else {
      return `Card ${method.account?.slice(-4) || ''}`;
    }
  }
}
